import { Boxes } from "lucide-react";
import GlassContainer from "@/shared/components/glasscontainer";
import { WorkspaceCard } from "./workspace_card";
import type { WorkspaceSummary } from "../models/Workspace";

export interface WorkspacesGridProps {
  workspaces: WorkspaceSummary[];
  loading: boolean;
  error: Error | null;
  deletingId: string | null;
  onOpen: (workspaceId: string) => void;
  onDashboard: (workspaceId: string) => void;
  onEdit: (workspace: WorkspaceSummary) => void;
  onDelete: (workspace: WorkspaceSummary) => void;
  onCreate: () => void;
}

export function WorkspacesGrid({
  workspaces,
  loading,
  error,
  deletingId,
  onOpen,
  onDashboard,
  onEdit,
  onDelete,
  onCreate,
}: WorkspacesGridProps) {
  if (error) {
    return (
      <GlassContainer className="mb-6 border-l-4 border-red-500 bg-red-500/5 p-6">
        <p className="font-medium text-red-700">Error al cargar workspaces</p>
        <p className="mt-1 text-sm text-red-600/80">{error.message}</p>
      </GlassContainer>
    );
  }

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <GlassContainer key={i} className="h-44 animate-pulse bg-white/20" />
        ))}
      </div>
    );
  }

  if (workspaces.length === 0) {
    return (
      <GlassContainer className="flex flex-col items-center justify-center p-12 text-center">
        <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-accent/10">
          <Boxes className="size-8 text-accent/50" />
        </div>
        <h3 className="mb-2 text-lg font-semibold text-foreground">Aún no hay workspaces</h3>
        <p className="mb-6 max-w-md text-sm text-foreground/50">
          Agrupá las automatizaciones de una misma aplicación en un workspace y montale un dashboard
          único con los KPIs de todas ellas.
        </p>
        <button
          onClick={onCreate}
          className="cursor-pointer rounded-xl bg-accent px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-accent/25 transition-all hover:bg-accent/90"
        >
          Crear workspace
        </button>
      </GlassContainer>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {workspaces.map((workspace) => (
        <WorkspaceCard
          key={workspace.id}
          workspace={workspace}
          deleting={deletingId === workspace.id}
          onOpen={() => onOpen(workspace.id)}
          onDashboard={() => onDashboard(workspace.id)}
          onEdit={() => onEdit(workspace)}
          onDelete={() => onDelete(workspace)}
        />
      ))}
    </div>
  );
}
